import React, { useState, useEffect } from 'react';
import { canEdit } from '../utils/permissions';
import { customerService } from '../services/customerService';
import { vehicleService } from '../services/vehicleService'; 
import { asService } from '../services/asService'; // 🚀 [추가] AS/부품판매 실시간 데이터 연동용
import { printReport } from '../utils/printUtils';

import CustomerHistoryModal from '../components/customer/CustomerHistoryModal';
import CustomerRepairModal from '../components/customer/CustomerRepairModal';
import CustomerPartsModal from '../components/customer/CustomerPartsModal';

const Customer = () => {
  const [customers, setCustomers] = useState([]); 
  const [vehicles, setVehicles] = useState([]); 
  const [asRecords, setAsRecords] = useState([]); // 🚀 [추가] AS 및 부품판매 전체 데이터 저장용 상태
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  // 복합 필터 상태 관리
  const [filterType, setFilterType] = useState('전체');        
  const [filterCarModel, setFilterCarModel] = useState('전체'); 

  // 히스토리 및 신규 조회 모달 상태 관리
  const [historyModal, setHistoryModal] = useState({ isOpen: false, customer: null });
  const [repairModal, setRepairModal] = useState({ isOpen: false, customer: null });
  const [partsModal, setPartsModal] = useState({ isOpen: false, customer: null });

  const initialForm = { 
    id: null, name: '', phone: '', carNumber: '', vin: '', 
    address: '', type: '개인', purchaseType: '신차', subsidy: '수령완료', 
    warrantyExpiry: '', memo: '', histories: [] 
  };
  const [formData, setFormData] = useState(initialForm);

  const refreshData = async () => {
    setLoading(true);
    try {
      const data = await customerService.getCustomers();
      setCustomers(data || []);
      const vData = await vehicleService.getVehicles();
      setVehicles(vData || []);
      
      // 🚀 [추가] 중앙 AS 데이터베이스로부터 실시간 데이터 로드
      const asData = await asService.getRecords();
      setAsRecords(asData || []);
    } catch (err) {
      console.error("데이터 로딩 에러:", err);
    }
    setLoading(false);
  };

  useEffect(() => { refreshData(); }, []);

  const onSave = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.address) {
      return alert("고객명, 연락처, 주소는 필수 입력 사항입니다.");
    }

    if (formData.type === '개인' || formData.type === '법인') {
      if (!formData.carNumber || !formData.carNumber.trim()) {
        return alert("❌ 개인 및 법인 고객은 반드시 '차량번호'를 입력해야 합니다.");
      }
      if (!formData.vin || !formData.vin.trim()) {
        return alert("❌ 개인 및 법인 고객은 반드시 '차대번호(VIN)'를 입력해야 합니다.");
      }

      const isVehicleValid = vehicles.some(
        v => String(v.carNumber).trim() === String(formData.carNumber).trim() && 
             String(v.vin).trim() === String(formData.vin).trim()
      );

      if (!isVehicleValid) {
        return alert("⚠️ 입력하신 차량번호와 차대번호가 차량관리 마스터 DB에 존재하지 않거나 일치하지 않습니다.\n'차량 관리' 메뉴에서 차량 정보를 먼저 등록한 후 고객을 등록해 주세요.");
      }
    }

    setLoading(true);
    try {
      if (isEditing) await customerService.updateCustomer(formData);
      else await customerService.addCustomer(formData);
      alert("✅ 저장이 완료되었습니다.");
      setFormData(initialForm);
      setIsEditing(false);
      await refreshData();
    } catch (err) { alert("저장 실패"); }
    setLoading(false);
  };

  const handleEdit = (c) => {
    setFormData({ ...initialForm, ...c });
    setIsEditing(true);
    window.scrollTo(0, 0);
  };

  const handleSaveHistory = async (updatedHistories) => {
    try {
      const updatedCustomer = { ...historyModal.customer, histories: updatedHistories };
      await customerService.updateCustomer(updatedCustomer); 
      
      setHistoryModal({ ...historyModal, customer: updatedCustomer }); 
      await refreshData(); 
    } catch (error) {
      alert("히스토리 저장 중 오류가 발생했습니다.");
    }
  };

  const displayValue = (val, isPhone = false) => {
    if (val === undefined || val === null || val === '' || val === 'undefined') return '-';
    if (isPhone) {
      let phoneStr = String(val).trim();
      if (phoneStr.startsWith('1')) return '0' + phoneStr;
      return phoneStr;
    }
    return val;
  };

  const getActualModelName = (carNumber) => {
    if (!carNumber) return '-';
    const matchedVehicle = vehicles.find(v => v.carNumber === carNumber);
    return matchedVehicle && matchedVehicle.modelName ? matchedVehicle.modelName : '-';
  };

  const handlePrintCustomer = () => {
    const title = "등록 고객 마스터 현황 내역서";
    const headers = ["차종", "고객명", "구분", "연락처", "주소", "차량번호"];
    const rows = filteredCustomers.map(c => [
      getActualModelName(c.carNumber),
      c.name || '',
      c.type || '개인',
      displayValue(c.phone, true),
      c.address || '',
      c.carNumber || '-'
    ]);

    const metaInfo = {
      "고객 구분": filterType,
      "선택 차종": filterCarModel,
      "검색어": searchTerm ? `"${searchTerm}"` : "전체 명부"
    };

    printReport(title, headers, rows, metaInfo);
  };

  const handleDownloadCustomerExcel = () => {
    if (filteredCustomers.length === 0) {
      alert("다운로드할 고객 데이터가 없습니다.");
      return;
    }

    const headers = ["차종", "고객명", "구분", "연락처", "주소", "차량번호", "차대번호"];
    const rows = filteredCustomers.map(c => [
      `"${String(getActualModelName(c.carNumber))}"`,
      `"${String(c.name || '').replace(/"/g, '""')}"`,
      `"${String(c.type || '개인')}"`,
      `"${String(c.phone || '')}"`,
      `"${String(c.address || '').replace(/"/g, '""')}"`,
      `"${String(c.carNumber || '')}"`,
      `"${String(c.vin || '')}"`
    ]);

    const csvContent = "\uFEFF" + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `고객_마스터_목록_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const safeCustomers = customers || [];
  const filteredCustomers = safeCustomers.filter(c => {
    const nameStr = String(c.name || '').toLowerCase();
    const carNumStr = String(c.carNumber || '').toLowerCase();
    const searchLow = searchTerm.toLowerCase();
    const matchSearch = nameStr.includes(searchLow) || carNumStr.includes(searchLow);

    const matchType = filterType === '전체' || String(c.type) === filterType;

    const actualModelName = getActualModelName(c.carNumber);
    const dbModelClean = actualModelName.replace(/\s+/g, '').toLowerCase();
    const selectedModelClean = filterCarModel.replace(/\s+/g, '').toLowerCase();
    const matchCarModel = filterCarModel === '전체' || dbModelClean === selectedModelClean || dbModelClean.includes(selectedModelClean);

    return matchSearch && matchType && matchCarModel;
  });

  const uniqueCarModels = Array.from(
    new Set(
      safeCustomers
        .map(c => getActualModelName(c.carNumber))
        .filter(model => model !== '-')
    )
  );

  const style = {
    wrapper: { padding: '40px 100px', background: '#f8fafc', minHeight: '100vh' },
    card: { background: 'white', padding: '35px', borderRadius: '20px', boxShadow: '0 4px 25px rgba(0,0,0,0.05)', marginBottom: '30px' },
    input: { width: '100%', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '10px', boxSizing: 'border-box', outline: 'none' },
    label: { fontSize: '12px', fontWeight: 'bold', color: '#475569', marginBottom: '5px', display: 'block' },
    grid4: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', marginBottom: '20px' },
    btnPrimary: { padding: '15px 40px', background: '#1e293b', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer' },
    btnGhost: { padding: '10px 20px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', cursor: 'pointer', color: '#64748b' },
    actionPrintBtn: { padding: '10px 18px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', fontSize: '13px' },
    actionExcelBtn: { padding: '10px 18px', background: '#10b981', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', fontSize: '13px' },
    filterSelect: { padding: '10px 15px', border: '2px solid #3b82f6', borderRadius: '8px', background: 'white', outline: 'none', cursor: 'pointer', fontWeight: '500', width: '140px' }
  };

  return (
    <div style={style.wrapper}>
      <div style={style.card}>
        <h2 style={{ margin: '0 0 25px 0' }}>👥 고객 마스터 관리 (Cloud)</h2>
        <form onSubmit={onSave}>
          <div style={style.grid4}>
            <div><label style={style.label}>1. 고객명 *</label><input style={style.input} value={formData.name} onChange={e=>setFormData({...formData, name:e.target.value})} /></div>
            <div><label style={style.label}>2. 연락처 *</label><input style={style.input} value={formData.phone} onChange={e=>setFormData({...formData, phone:e.target.value})} /></div>
            <div><label style={style.label}>3. 차량번호</label><input style={style.input} value={formData.carNumber} onChange={e=>setFormData({...formData, carNumber:e.target.value})} /></div>
            <div><label style={style.label}>4. 차대번호(VIN)</label><input style={style.input} value={formData.vin} onChange={e=>setFormData({...formData, vin:e.target.value})} /></div>
          </div>
          <div style={style.grid4}>
            <div><label style={style.label}>5. 주소 *</label><input style={style.input} value={formData.address} onChange={e=>setFormData({...formData, address:e.target.value})} /></div>
            <div><label style={style.label}>6. 고객구분</label>
              <select style={style.input} value={formData.type} onChange={e=>setFormData({...formData, type:e.target.value})}>
                <option value="개인">개인</option>
                <option value="법인">법인</option>
                <option value="정비업체">정비업체</option>
              </select>
            </div>
            <div><label style={style.label}>7. 구매유형</label>
              <select style={style.input} value={formData.purchaseType} onChange={e=>setFormData({...formData, purchaseType:e.target.value})}>
                <option value="신차">신차</option><option value="중고차">중고차</option>
              </select>
            </div>
            <div><label style={style.label}>8. 보조금</label>
              <select style={style.input} value={formData.subsidy} onChange={e=>setFormData({...formData, subsidy:e.target.value})}>
                <option value="수령완료">수령완료</option><option value="미수령">미수령</option>
              </select>
            </div>
          </div>
          <div style={style.grid4}>
            <div><label style={style.label}>9. 보증만료일</label><input type="date" style={style.input} value={formData.warrantyExpiry} onChange={e=>setFormData({...formData, warrantyExpiry:e.target.value})} /></div>
            <div style={{gridColumn:'span 2'}}><label style={style.label}>10. 메모</label><input style={style.input} value={formData.memo} onChange={e=>setFormData({...formData, memo:e.target.value})} /></div>
            <div style={{display:'flex', alignItems:'flex-end', justifyContent:'flex-end'}}>
               {isEditing && <button type="button" onClick={()=>{setFormData(initialForm); setIsEditing(false);}} style={{...style.btnGhost, marginRight:'10px'}}>취소</button>}
               {canEdit() && <button type="submit" disabled={loading} style={style.btnPrimary}>{isEditing ? "정보 수정" : "고객 정보 저장"}</button>}
            </div>
          </div>
        </form>
      </div>

      <div style={style.card}>
        <div style={{display:'flex', justifyContent:'space-between', marginBottom:'20px', alignItems:'center'}}>
          <h3>📦 등록 고객 목록 (총 {filteredCustomers.length}명)</h3>
          
          <div style={{display:'flex', gap:'10px', alignItems:'center'}}>
            <button style={style.actionPrintBtn} onClick={handlePrintCustomer}>🖨️ 출력하기</button>
            <button style={style.actionExcelBtn} onClick={handleDownloadCustomerExcel}>📊 EXCEL 다운로드</button>

            <select style={style.filterSelect} value={filterType} onChange={e => setFilterType(e.target.value)}>
              <option value="전체">전체 구분</option><option value="개인">개인</option><option value="법인">법인</option><option value="정비업체">정비업체</option>
            </select>

            <select style={{ ...style.filterSelect, border: '2px solid #10b981' }} value={filterCarModel} onChange={e => setFilterCarModel(e.target.value)}>
              <option value="전체">전체 차종</option>
              {uniqueCarModels.map((model, idx) => (
                <option key={idx} value={model}>{model}</option>
              ))}
            </select>

            <input style={{padding:'10px', width:'230px', borderRadius:'8px', border:'1px solid #ddd', outline:'none'}} placeholder="이름 또는 차량번호 검색..." value={searchTerm} onChange={e=>setSearchTerm(e.target.value)} />
          </div>
        </div>

        <div style={{maxHeight:'500px', overflowY:'auto'}}>
          <table style={{width:'100%', borderCollapse:'collapse'}}>
            <thead style={{position:'sticky', top:0, background:'white', zIndex:10}}>
              <tr style={{textAlign:'left', borderBottom:'2px solid #eee', color:'#666', fontSize:'13px'}}>
                <th style={{padding:'15px'}}>차종</th>
                <th style={{padding:'15px'}}>고객명(구분)</th>
                <th style={{padding:'15px'}}>연락처</th>
                <th style={{padding:'15px'}}>주소</th>
                <th style={{padding:'15px'}}>차량번호</th>
                <th style={{padding:'15px'}}>차대번호</th>
                <th style={{padding:'15px', textAlign: 'center'}}>관리</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map((c, idx) => (
                <tr key={c.id} style={{borderBottom:'1px solid #f1f5f9'}}>
                  <td style={{padding:'15px', fontWeight:'bold', color:'#3b82f6'}}>{getActualModelName(c.carNumber)}</td>
                  <td style={{padding:'15px'}}><b>{displayValue(c.name)}</b><br/><span style={{fontSize:'12px', color:'#94a3b8'}}>{displayValue(c.type)}</span></td>
                  <td style={{padding:'15px'}}>{displayValue(c.phone, true)}</td>
                  <td style={{padding:'15px', fontSize:'13px', color:'#475569'}}>{displayValue(c.address)}</td>
                  <td style={{padding:'15px'}}><span style={{background:'#f1f5f9', padding:'3px 8px', borderRadius:'5px', fontWeight:'bold'}}>{displayValue(c.carNumber)}</span></td>
                  {/* 🚀 [버그 수정 완료] vin을 c.vin으로 정확하게 지정하여 ReferenceError 완전 방지 */}
                  <td style={{padding:'15px', fontSize:'12px', color:'#64748b'}}>{displayValue(c.vin)}</td>
                  <td style={{padding:'15px', textAlign:'center'}}>
                    <div style={{display:'flex', gap:'5px', justifyContent:'center', flexWrap:'nowrap'}}>
                      <button onClick={() => handleEdit(c)} style={{border:'none', background:'#f1f5f9', padding:'5px 12px', borderRadius:'6px', cursor:'pointer', fontWeight:'bold', color:'#475569'}}>수정</button>
                      
                      <button onClick={() => setHistoryModal({ isOpen: true, customer: c })} style={{border:'1px solid #d8b4fe', background:'#faf5ff', padding:'5px 12px', borderRadius:'6px', cursor:'pointer', fontWeight:'bold', color:'#9333ea'}}>히스토리</button>
                      
                      <button onClick={() => setRepairModal({ isOpen: true, customer: c })} style={{border:'1px solid #bfdbfe', background:'#eff6ff', padding:'5px 12px', borderRadius:'6px', cursor:'pointer', fontWeight:'bold', color:'#2563eb'}}>정비현황</button>
                      <button onClick={() => setPartsModal({ isOpen: true, customer: c })} style={{border:'1px solid #fef08a', background:'#fefce8', padding:'5px 12px', borderRadius:'6px', cursor:'pointer', fontWeight:'bold', color:'#ca8a04'}}>부품판매</button>
                      
                      {canEdit() && <button onClick={async () => {if(window.confirm('삭제할까요?')) { await customerService.deleteCustomer(c.id); refreshData(); }}} style={{border:'none', background:'none', color:'#ef4444', cursor:'pointer', fontWeight:'bold'}}>삭제</button>}
                    </div>
                  </td>
                </tr>
              ))}
              {filteredCustomers.length === 0 && (
                <tr>
                  <td colSpan="7" style={{padding:'40px', textAlign:'center', color:'#94a3b8', fontWeight:'bold'}}>
                    조건에 맞는 고객 데이터가 존재하지 않습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CustomerHistoryModal 
        isOpen={historyModal.isOpen} 
        customer={historyModal.customer} 
        onClose={() => setHistoryModal({ isOpen: false, customer: null })}
        onSaveHistory={handleSaveHistory}
      />

      <CustomerRepairModal 
        isOpen={repairModal.isOpen} 
        customer={repairModal.customer} 
        asRecords={asRecords}
        onClose={() => setRepairModal({ isOpen: false, customer: null })}
      />

      <CustomerPartsModal 
        isOpen={partsModal.isOpen} 
        customer={partsModal.customer} 
        asRecords={asRecords}
        onClose={() => setPartsModal({ isOpen: false, customer: null })}
      />
    </div>
  );
};

export default Customer;